import { Link, Navigate, Outlet, useParams } from "@tanstack/react-router"
import { Boxes, Lock, RotateCw, Search } from "lucide-react"
import { useMemo, useState } from "react"
import { Trans } from "react-i18next"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { type Artifact, artifactTitle, entityDef, kindLabel, type Layer } from "@/lib/defs"
import { useFormatNumber, useT } from "@/lib/i18n"
import { useDefinitions } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"

/** `/definitions` lands on the first installed kind. */
export function DefinitionsIndex() {
  const t = useT()
  const scope = useScope()
  const defs = useDefinitions(scope)
  if (defs.isPending) return <TableSkeleton />
  if (defs.isError)
    return <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
  const first = defs.data.kinds[0]
  if (!first) {
    return (
      <EmptyState icon={<Boxes />} title={t("definitions.emptyTitle")}>
        <p>
          <Trans
            shouldUnescape
            tOptions={{ interpolation: { escapeValue: true } }}
            t={t}
            i18nKey="definitions.emptyDescription"
            components={{ route: <code /> }}
          />
        </p>
      </EmptyState>
    )
  }
  return <Navigate to="/$orgId/$environmentId/definitions/$kind" params={{ ...scope, kind: first.kind }} replace />
}

type LayerFilter = "all" | Layer

function matches(a: Artifact, needle: string): boolean {
  if (!needle) return true
  const n = needle.toLowerCase()
  return (
    a.artifactId.toLowerCase().includes(n) ||
    artifactTitle(a).toLowerCase().includes(n) ||
    (a.namespace?.toLowerCase().includes(n) ?? false)
  )
}

function Segmented({
  value,
  onChange,
  counts,
}: {
  value: LayerFilter
  onChange: (v: LayerFilter) => void
  counts: Record<LayerFilter, number>
}) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const options: { id: LayerFilter; label: string }[] = [
    { id: "all", label: t("common.all") },
    { id: "tenant", label: t("common.tenant") },
    { id: "registry", label: t("common.registry") },
  ]
  return (
    <div
      role="radiogroup"
      aria-label={t("definitions.layer")}
      className="inline-flex h-7 rounded-md border border-border-subtle bg-background p-0.5"
    >
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={cn(
            "inline-flex items-center gap-1 rounded-[5px] px-2 text-xs text-muted-foreground transition-colors duration-150 hover:text-foreground",
            value === o.id && "bg-muted text-foreground",
          )}
        >
          {o.label}
          <span className="text-2xs text-subtle-foreground tabular">{formatNumber(counts[o.id])}</span>
        </button>
      ))}
    </div>
  )
}

function ArtifactRow({ artifact, kind }: { artifact: Artifact; kind: string }) {
  const t = useT()
  const scope = useScope()
  const def = artifact.kind === "entity" ? entityDef(artifact) : null
  return (
    <Link
      to="/$orgId/$environmentId/definitions/$kind/$artifactId"
      params={{ ...scope, kind, artifactId: artifact.artifactId }}
      className="group flex items-center gap-3 px-4 py-2 transition-colors duration-100 hover:bg-raised/60 data-[status=active]:bg-raised"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm text-foreground">{artifactTitle(artifact)}</span>
          {def && def.form !== "objectType" ? <Badge tone="outline">{def.form}</Badge> : null}
        </div>
        <code className="block truncate font-mono text-2xs text-subtle-foreground">{artifact.artifactId}</code>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {artifact.layer === "registry" ? (
          <Lock className="size-3 text-subtle-foreground" aria-label={t("definitions.registryReadOnly")} />
        ) : null}
        <code className="font-mono text-2xs text-muted-foreground tabular">{artifact.version}</code>
      </div>
    </Link>
  )
}

export function DefinitionsKindLayout() {
  const t = useT()
  const scope = useScope()
  const { kind } = useParams({ from: "/$orgId/$environmentId/definitions/$kind" })
  const { artifactId } = useParams({ strict: false })
  const defs = useDefinitions(scope)
  const [query, setQuery] = useState("")
  const [layer, setLayer] = useState<LayerFilter>("all")

  const ofKind = useMemo(() => defs.data?.artifacts.filter((a) => a.kind === kind) ?? [], [defs.data, kind])
  const counts = useMemo(() => {
    const searched = ofKind.filter((a) => matches(a, query))
    return {
      all: searched.length,
      tenant: searched.filter((a) => a.layer === "tenant").length,
      registry: searched.filter((a) => a.layer === "registry").length,
    }
  }, [ofKind, query])
  const visible = useMemo(
    () =>
      ofKind
        .filter((a) => matches(a, query) && (layer === "all" || a.layer === layer))
        .sort((a, b) =>
          a.layer === b.layer ? artifactTitle(a).localeCompare(artifactTitle(b)) : a.layer === "tenant" ? -1 : 1,
        ),
    [ofKind, query, layer],
  )

  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="d" to="/$orgId/$environmentId/definitions" params={scope}>
            {t("nav.definitions")}
          </Link>,
          kindLabel(kind, t),
        ]}
        actions={
          <>
            {defs.data ? (
              <span className="font-mono text-xs text-muted-foreground tabular" title={t("definitions.setVersion")}>
                {t("definitions.installedVersion", { version: defs.data.version })}
              </span>
            ) : null}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => void defs.refetch()}
              disabled={defs.isFetching}
              aria-label={t("definitions.refresh")}
            >
              <RotateCw className={defs.isFetching ? "animate-spin" : undefined} />
            </Button>
          </>
        }
      />
      <Screen
        width="full"
        className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto px-4 py-6 md:flex-row md:space-y-0 md:px-6"
      >
        <section
          aria-label={kindLabel(kind, t)}
          className={cn("flex min-h-48 w-full shrink-0 flex-col md:w-72", artifactId && "max-md:hidden")}
        >
          <Section className="flex min-h-0 flex-1 flex-col" contentClassName="flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex flex-col gap-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute top-1/2 start-2.5 size-3.5 -translate-y-1/2 text-subtle-foreground"
                  aria-hidden
                />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t("definitions.filter", { kind: kindLabel(kind, t).toLowerCase() })}
                  aria-label={t("definitions.filter", { kind: kindLabel(kind, t).toLowerCase() })}
                  className="ps-8"
                />
              </div>
              <Segmented value={layer} onChange={setLayer} counts={counts} />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {defs.isPending ? (
                <TableSkeleton columns={2} />
              ) : defs.isError ? (
                <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
              ) : ofKind.length === 0 ? (
                <EmptyState
                  icon={<Boxes />}
                  title={t("definitions.emptyKindTitle", { kind: kindLabel(kind, t).toLowerCase() })}
                >
                  <p>
                    <Trans
                      shouldUnescape
                      tOptions={{ interpolation: { escapeValue: true } }}
                      t={t}
                      i18nKey="definitions.emptyKindDescription"
                      values={{ kind, orgId: scope.orgId, environmentId: scope.environmentId }}
                      components={{ kind: <code /> }}
                    />
                  </p>
                </EmptyState>
              ) : visible.length === 0 ? (
                <p className="px-4 py-6 text-sm text-muted-foreground">{t("definitions.noMatches")}</p>
              ) : (
                <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
                  {visible.map((a) => (
                    <li key={`${a.artifactId}@${a.version}`}>
                      <ArtifactRow artifact={a} kind={kind} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Section>
        </section>
        <section className={cn("min-w-0 flex-1 md:overflow-y-auto", !artifactId && "max-md:hidden")}>
          <Outlet />
        </section>
      </Screen>
    </>
  )
}

export function DefinitionsKindIndex() {
  const t = useT()
  const { kind } = useParams({ from: "/$orgId/$environmentId/definitions/$kind" })
  return (
    <div className="flex h-full items-start p-6">
      <p className="max-w-xs text-start text-sm text-subtle-foreground">
        {t("definitions.select", { kind: kindLabel(kind, t).toLowerCase() })}
      </p>
    </div>
  )
}
